// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Landing Page
// Marketing page with age gate redirect
// ================================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div style={styles.container}>
      {/* Hero */}
      <div style={styles.hero}>
        <span style={styles.heroIcon}>🔥</span>
        <h1 style={styles.heroTitle}>FORGE CASINO</h1>
        <p style={styles.heroTagline}>THE WORLD'S FIRST PROVABLY FAIR $FORGE CASINO</p>
        <p style={styles.heroSub}>
          Every spin is cryptographically verified. The casino mathematically CANNOT cheat.
          Every bet, every win, every payout — on-chain, auditable, transparent.
        </p>
        <button style={styles.enterBtn} onClick={() => navigate('/age-verify')}>
          🎰 ENTER CASINO
        </button>
        <span style={styles.ageNote}>18+ | Provably Fair | AGLC Compliant</span>
      </div>

      {/* Features */}
      <div style={styles.features}>
        <div style={styles.feature}>
          <span style={styles.featureIcon}>🔒</span>
          <h3 style={styles.featureTitle}>PROVABLY FAIR</h3>
          <p style={styles.featureText}>SHA-256 cryptographic hashing. Result committed before you spin. Verify any spin, any time.</p>
        </div>
        <div style={styles.feature}>
          <span style={styles.featureIcon}>⛓️</span>
          <h3 style={styles.featureTitle}>BLOCKCHAIN VERIFIED</h3>
          <p style={styles.featureText}>Every transaction recorded on-chain. Full audit trail. Regulators can verify in real-time.</p>
        </div>
        <div style={styles.feature}>
          <span style={styles.featureIcon}>💰</span>
          <h3 style={styles.featureTitle}>$FORGE POWERED</h3>
          <p style={styles.featureText}>Play with $FORGE credits. Use across the entire Forge ecosystem. The most diversified utility token ever.</p>
        </div>
        <div style={styles.feature}>
          <span style={styles.featureIcon}>🎰</span>
          <h3 style={styles.featureTitle}>DIGITAL SLOTS</h3>
          <p style={styles.featureText}>Classic and video slots. Progressive jackpots. Sports betting. Fantasy leagues. More games coming.</p>
        </div>
        <div style={styles.feature}>
          <span style={styles.featureIcon}>🛡️</span>
          <h3 style={styles.featureTitle}>RESPONSIBLE GAMING</h3>
          <p style={styles.featureText}>Set deposit limits, loss limits, time limits. Self-exclude anytime. Help resources always available.</p>
        </div>
        <div style={styles.feature}>
          <span style={styles.featureIcon}>🏆</span>
          <h3 style={styles.featureTitle}>SPORTS & FANTASY</h3>
          <p style={styles.featureText}>Bet on sports. Host fantasy leagues. All on $FORGE. All provably fair. All on-chain.</p>
        </div>
      </div>

      {/* How It Works */}
      <div style={styles.howSection}>
        <h2 style={styles.howTitle}>HOW PROVABLY FAIR WORKS</h2>
        <div style={styles.howSteps}>
          <div style={styles.howStep}>
            <div style={styles.stepCircle}>1</div>
            <p style={styles.stepText}>Casino commits to result with SHA-256 hash BEFORE you spin</p>
          </div>
          <div style={styles.stepArrow}>→</div>
          <div style={styles.howStep}>
            <div style={styles.stepCircle}>2</div>
            <p style={styles.stepText}>You spin — your browser adds randomness</p>
          </div>
          <div style={styles.stepArrow}>→</div>
          <div style={styles.howStep}>
            <div style={styles.stepCircle}>3</div>
            <p style={styles.stepText}>Verify the hash — if it matches, the spin was fair</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <p style={styles.footerText}>
          🔥 Powered by Miskulin Technologies | The Forge Standard
        </p>
        <p style={styles.footerSmall}>
          Gambling should be fun. If you have a problem, call 1-866-332-2322.
          Must be 18+. $FORGE Casino is a product of Miskulin Technologies Inc.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#0a0a0a' },
  hero: {
    textAlign: 'center', padding: '60px 20px 40px',
    background: 'linear-gradient(180deg, #1a0000 0%, #0a0000 50%, #0a0a0a 100%)',
  },
  heroIcon: { fontSize: 64 },
  heroTitle: {
    fontSize: 36, fontWeight: 900, color: '#DC2626', letterSpacing: 6,
    fontFamily: "'Courier New', monospace", margin: '12px 0 8px',
    textShadow: '0 0 20px #DC262644',
  },
  heroTagline: {
    fontSize: 12, color: '#00FF88', letterSpacing: 3, fontWeight: 700, marginBottom: 16,
  },
  heroSub: {
    fontSize: 14, color: '#aaa', maxWidth: 500, margin: '0 auto 24px', lineHeight: 1.7,
  },
  enterBtn: {
    padding: '16px 40px', borderRadius: 12, border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #991B1B 100%)',
    color: '#fff', fontSize: 18, fontWeight: 900, letterSpacing: 3,
    cursor: 'pointer', boxShadow: '0 0 30px #DC262644, 0 8px 24px rgba(0,0,0,0.5)',
    display: 'inline-block',
  },
  ageNote: {
    display: 'block', fontSize: 10, color: '#555', marginTop: 12, letterSpacing: 1,
  },
  features: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16, padding: '40px 20px', maxWidth: 800, margin: '0 auto',
  },
  feature: {
    padding: 20, background: '#111', borderRadius: 12, border: '1px solid #222', textAlign: 'center',
  },
  featureIcon: { fontSize: 32, display: 'block', marginBottom: 8 },
  featureTitle: { fontSize: 12, color: '#DC2626', fontWeight: 900, letterSpacing: 2, marginBottom: 6 },
  featureText: { fontSize: 11, color: '#888', lineHeight: 1.6 },
  howSection: {
    padding: '40px 20px', maxWidth: 700, margin: '0 auto', textAlign: 'center',
  },
  howTitle: { fontSize: 16, color: '#00FF88', fontWeight: 900, letterSpacing: 3, marginBottom: 24 },
  howSteps: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap',
  },
  howStep: { maxWidth: 180, textAlign: 'center' },
  stepCircle: {
    width: 40, height: 40, borderRadius: '50%', background: '#DC2626', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18, fontWeight: 900, margin: '0 auto 8px',
  },
  stepText: { fontSize: 11, color: '#aaa', lineHeight: 1.5 },
  stepArrow: { fontSize: 20, color: '#333' },
  footer: { textAlign: 'center', padding: '40px 20px', borderTop: '1px solid #1a1a1a' },
  footerText: { fontSize: 12, color: '#555' },
  footerSmall: { fontSize: 9, color: '#333', marginTop: 8, lineHeight: 1.6 },
};
